/**
 * 国土地理院ベクトルタイル (基盤地図情報) から建物フットプリントを取得する。
 *
 * Mapbox (OSM 由来) に無い建物も、基盤地図情報の建築物面はほぼ全国を網羅する
 * (実測: 同一ビューで Mapbox ~4,400棟 に対し GSI ~10,000棟)。Mapbox の敷地・建物の
 * どちらも取れない施設の最後の受け皿として使う。
 *
 * - タイル: experimental_bvmap (トークン不要・出典明記で利用可)。z16 に建築物面。
 * - 読み方は Mapbox プローブと同じ「画面外プローブ地図 + querySourceFeatures」。
 *   ただし mapbox-gl はトークン必須のため **maplibre-gl** を使う (依存済み・動的 import
 *   でメインバンドルを太らせない)。
 * - GSI の建物フィーチャは feature id を持たないため、同 id union は不可。
 *   小さな建物ではタイル分割はまれなので、内包片をそのまま使う。
 * - **近傍救済**: 代表点が建物でなく庭・境内に落ちる施設が多い (実測 22/40)。
 *   内包が無ければ 30m 以内の最近傍建物を採用する (半径は誤建物リスクとの妥協点)。
 */
import type { LngLat } from '@/types/property';
import {
  pointToRingDistanceM,
  type TileBuildingFeature,
} from '@/lib/map/building-footprint';
import { booleanPointInPolygon } from '@turf/turf';
import type { Feature, Point } from 'geojson';

const GSI_TILES =
  'https://cyberjapandata.gsi.go.jp/xyz/experimental_bvmap/{z}/{x}/{y}.pbf';
const PROBE_ZOOM = 16;
const READY_TIMEOUT_MS = 12_000;
const IDLE_TIMEOUT_MS = 8_000;
const INACTIVITY_MS = 90_000;
/** 近傍救済の既定半径 (m)。 */
export const GSI_NEAR_METERS = 30;

// maplibre の型は動的 import 先にしか無いので、必要最小限のダックタイプで扱う。
interface ProbeMap {
  jumpTo(o: { center: [number, number]; zoom: number }): void;
  once(ev: string, cb: (e?: unknown) => void): void;
  off(ev: string, cb: (e?: unknown) => void): void;
  querySourceFeatures(
    src: string,
    o: { sourceLayer: string },
  ): TileBuildingFeature[];
  remove(): void;
  getContainer(): HTMLElement;
}

let probe: ProbeMap | null = null;
let probeReady: Promise<ProbeMap> | null = null;
let chain: Promise<unknown> = Promise.resolve();
let idleTimer: ReturnType<typeof setTimeout> | null = null;

async function createProbe(): Promise<ProbeMap> {
  const ml = (await import('maplibre-gl')).default;
  const holder = document.createElement('div');
  holder.style.cssText =
    'position:fixed;left:-99999px;top:0;width:512px;height:512px;pointer-events:none;';
  document.body.appendChild(holder);
  const map = new ml.Map({
    container: holder,
    style: {
      version: 8,
      sources: {
        gsi: {
          type: 'vector',
          tiles: [GSI_TILES],
          minzoom: 4,
          maxzoom: 16,
          attribution: '国土地理院',
        },
      },
      // タイルはソースを参照するレイヤーが無いとロードされないためダミーを置く。
      layers: [
        {
          id: 'dummy-building',
          type: 'fill',
          source: 'gsi',
          'source-layer': 'building',
          paint: { 'fill-opacity': 0 },
        },
      ],
    },
    center: [139.767, 35.681],
    zoom: PROBE_ZOOM,
    interactive: false,
    attributionControl: false,
  }) as unknown as ProbeMap;
  probe = map;
  return new Promise((resolve, reject) => {
    let done = false;
    const succeed = () => {
      if (!done) {
        done = true;
        resolve(map);
      }
    };
    const fail = (err: unknown) => {
      if (!done) {
        done = true;
        reject(err instanceof Error ? err : new Error('gsi probe error'));
      }
    };
    map.once('load', succeed);
    map.once('error', (e) => fail((e as { error?: Error })?.error));
    window.setTimeout(succeed, READY_TIMEOUT_MS);
  });
}

function waitIdle(map: ProbeMap): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      map.off('idle', finish);
      resolve();
    };
    map.once('idle', finish);
    window.setTimeout(finish, IDLE_TIMEOUT_MS);
  });
}

function scheduleIdleDispose(): void {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(disposeGsiProbe, INACTIVITY_MS);
}

/** 外周リング群 (Polygon/MultiPolygon の各外周) を列挙する。 */
function outerRingsOf(f: TileBuildingFeature): Array<Array<[number, number]>> {
  const g = f.geometry;
  if (!g) return [];
  if (g.type === 'Polygon') {
    const r = (g.coordinates as Array<Array<[number, number]>>)[0];
    return r ? [r] : [];
  }
  if (g.type === 'MultiPolygon') {
    return (g.coordinates as Array<Array<Array<[number, number]>>>)
      .map((poly) => poly[0])
      .filter((r): r is Array<[number, number]> => !!r);
  }
  return [];
}

async function doFetch(
  center: LngLat,
  nearMeters: number,
): Promise<Array<[number, number]> | null> {
  if (!probeReady) probeReady = createProbe();
  let map: ProbeMap;
  try {
    map = await probeReady;
  } catch {
    disposeGsiProbe();
    return null;
  }
  try {
    map.jumpTo({ center: [center.lng, center.lat], zoom: PROBE_ZOOM });
    await waitIdle(map);
    const feats = map.querySourceFeatures('gsi', { sourceLayer: 'building' });
    const pt: Feature<Point> = {
      type: 'Feature',
      properties: {},
      geometry: { type: 'Point', coordinates: [center.lng, center.lat] },
    };
    // 1. 内包する建物 (最小面積 = 最も具体的)
    let best: Array<[number, number]> | null = null;
    let bestArea = Infinity;
    // 2. 内包が無いときの近傍候補
    let near: Array<[number, number]> | null = null;
    let nearDist = Infinity;
    for (const f of feats) {
      for (const ring of outerRingsOf(f)) {
        if (ring.length < 4) continue;
        let contains = false;
        try {
          contains = booleanPointInPolygon(pt, {
            type: 'Feature',
            properties: {},
            geometry: { type: 'Polygon', coordinates: [ring] },
          });
        } catch {
          continue;
        }
        if (contains) {
          let a = 0;
          for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            a += (ring[j]![0] + ring[i]![0]) * (ring[j]![1] - ring[i]![1]);
          }
          a = Math.abs(a) / 2;
          if (a < bestArea) {
            bestArea = a;
            best = ring;
          }
        } else if (!best) {
          const d = pointToRingDistanceM([center.lng, center.lat], ring);
          if (d < nearDist) {
            nearDist = d;
            near = ring;
          }
        }
      }
    }
    scheduleIdleDispose();
    const picked = best ?? (nearDist <= nearMeters ? near : null);
    if (!picked) return null;
    const rounded = picked.map(
      ([x, y]) =>
        [Math.round(x * 1e6) / 1e6, Math.round(y * 1e6) / 1e6] as [
          number,
          number,
        ],
    );
    const first = rounded[0]!;
    const last = rounded[rounded.length - 1]!;
    if (first[0] !== last[0] || first[1] !== last[1]) {
      rounded.push([first[0], first[1]]);
    }
    return rounded;
  } catch {
    disposeGsiProbe();
    return null;
  }
}

/**
 * GSI (基盤地図情報) の建物フットプリントを取得。内包 > 近傍 nearMeters 以内。
 * 非ブラウザ環境では null。
 */
export function fetchGsiBuilding(
  center: LngLat,
  nearMeters: number = GSI_NEAR_METERS,
): Promise<Array<[number, number]> | null> {
  if (typeof document === 'undefined') return Promise.resolve(null);
  const run = chain.then(() => doFetch(center, nearMeters)).catch(() => null);
  chain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/** プローブ地図を破棄する (非アクティブ時に自動で呼ばれる)。 */
export function disposeGsiProbe(): void {
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
  try {
    probe?.remove();
    probe?.getContainer()?.remove();
  } catch {
    /* noop */
  }
  probe = null;
  probeReady = null;
  chain = Promise.resolve();
}
